// ============================================================================
// PsyResultsDashboard — ПАНЕЛЬ РЕЗУЛЬТАТОВ кабинета психолога.
//
// Один экран сводки по ВСЕМ пациентам (данные — psyResultsSummary, SSoT сервера):
//   • последние прохождения: пациент, методика, дата, балл/заключение;
//   • ожидающие и просроченные назначения (пациент ещё не прошёл методику);
//   • фильтры «Все / Требуют внимания / Завершены / Просрочены» + поиск;
//   • быстрый переход к карточке пациента и к его результатам;
//   • явные состояния: загрузка, ошибка (+ «Повторить»), пусто, нет прав.
//
// Никаких новых RPC: всё читается существующими методами метаданных. Панель
// только показывает и навигирует — назначение/внесение результата остаются на
// вкладке «Тесты и результаты».
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity, AlertTriangle, CheckCircle2, Clock3, FlaskConical, Hourglass, Loader2,
  RefreshCw, Search, UserRound, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { formatValue } from '@/lib/format';
import {
  loadPsyResultsSummary,
  type PsySummaryData,
  type PsySummaryFilter,
  type PsySummaryRow,
  type PsySummaryStatus,
} from '@/components/psy/psyResultsSummary';

const STATUS_LABEL: Record<PsySummaryStatus, string> = {
  attention: 'Требует внимания',
  completed: 'Завершён',
  overdue: 'Просрочен',
  pending: 'Ожидает',
};

const STATUS_CLASS: Record<PsySummaryStatus, string> = {
  attention: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  completed: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  overdue: 'border-destructive/40 bg-destructive/10 text-destructive',
  pending: 'border-primary/25 bg-primary/10 text-primary',
};

const STATUS_ICON: Record<PsySummaryStatus, typeof Activity> = {
  attention: AlertTriangle,
  completed: CheckCircle2,
  overdue: Clock3,
  pending: Hourglass,
};

const FILTERS: Array<{ id: PsySummaryFilter; label: string }> = [
  { id: 'all', label: 'Все' },
  { id: 'attention', label: 'Требуют внимания' },
  { id: 'completed', label: 'Завершены' },
  { id: 'overdue', label: 'Просрочены' },
];

const SLOW_LOAD_MS = 12000;

export interface PsyResultsDashboardProps {
  /** Переход к карточке пациента (диалог рабочего места). */
  onOpenPatient: (patientId: string, patientName: string) => void;
  /** Переход к результатам пациента (вкладка «Тесты и результаты»). */
  onOpenResult: (patientId: string, patientName: string) => void;
}

function statusCount(counts: PsySummaryData['counts'], filter: PsySummaryFilter): number {
  return filter === 'all' ? counts.all : counts[filter];
}

function shortText(value: string, limit = 120): string {
  const text = value.trim();
  return text.length <= limit ? text : `${text.slice(0, limit)}…`;
}

/** Что показать в колонке «Результат» — без выдуманных данных. */
function resultText(row: PsySummaryRow): string {
  if (row.kind === 'assignment') {
    return row.status === 'overdue'
      ? 'срок истёк — методика не пройдена'
      : 'ожидает прохождения';
  }
  const parts: string[] = [];
  if (row.score) parts.push(`балл: ${row.score}`);
  if (row.passed === true) parts.push('порог пройден');
  if (row.passed === false) parts.push('порог не пройден');
  if (row.conclusion) parts.push(shortText(row.conclusion, 90));
  return parts.join(' · ');
}

function matchesSearch(row: PsySummaryRow, query: string): boolean {
  if (!query) return true;
  const haystack = [
    row.patientName,
    row.methodName,
    row.methodCode,
    row.score,
    row.conclusion,
    STATUS_LABEL[row.status],
  ].join(' ').toLowerCase();
  return haystack.includes(query);
}

export function PsyResultsDashboard({ onOpenPatient, onOpenResult }: PsyResultsDashboardProps) {
  const [data, setData] = useState<PsySummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<PsySummaryFilter>('all');
  const [search, setSearch] = useState('');
  const sequence = useRef(0);

  const fetchData = useCallback(async () => {
    const current = ++sequence.current;
    try {
      const next = await loadPsyResultsSummary();
      if (sequence.current !== current) return;
      setData(next);
    } catch (e) {
      if (sequence.current !== current) return;
      logErrorToJournal(e, 'Психолог: сводка результатов');
      setError(friendlyError(e));
    } finally {
      if (sequence.current === current) {
        setLoading(false);
        setSlow(false);
      }
    }
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    setError('');
    setSlow(false);
    void fetchData();
  }, [fetchData]);

  // Первичная загрузка — после монтирования (не синхронно в теле эффекта).
  useEffect(() => {
    const timer = setTimeout(() => { void fetchData(); }, 0);
    return () => clearTimeout(timer);
  }, [fetchData]);
  useEffect(() => () => { sequence.current += 1; }, []);

  // «Не вечная загрузка»: если ответа долго нет — честная подсказка и повтор.
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => setSlow(true), SLOW_LOAD_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const query = search.trim().toLowerCase();
  const visibleRows = useMemo(() => {
    const byStatus = filter === 'all' ? rows : rows.filter((row) => row.status === filter);
    if (!query) return byStatus;
    return byStatus.filter((row) => matchesSearch(row, query));
  }, [rows, filter, query]);

  const resetFilters = useCallback(() => {
    setFilter('all');
    setSearch('');
  }, []);

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap shrink-0">
        <Activity className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Сводка результатов</span>
        {data && (
          <span className="text-[11px] text-muted-foreground">
            всего: {data.counts.all} · требуют внимания: {data.counts.attention} · просрочено: {data.counts.overdue}
            {data.counts.pending > 0 ? ` · ожидают: ${data.counts.pending}` : ''}
          </span>
        )}
        <Button size="sm" variant="outline" className="h-7 px-2 ml-auto"
          onClick={reload} disabled={loading} title="Обновить сводку">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>

      <div className="px-3 py-2 border-b space-y-2 shrink-0">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((item) => {
            const active = filter === item.id;
            const count = data ? statusCount(data.counts, item.id) : 0;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setFilter(item.id)}
                className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent/40'
                }`}
              >
                {item.label}
                <span className="ml-1 opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск: пациент, методика, балл, заключение…"
            className="h-8 pl-7 pr-8 text-xs"
          />
          {search && (
            <button
              type="button"
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
              onClick={() => setSearch('')}
              title="Очистить поиск"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-3 space-y-3">
        {loading && !data && (
          <div className="text-xs text-muted-foreground border rounded-lg px-3 py-2 space-y-1">
            <div className="flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Загрузка сводки результатов…
            </div>
            {slow && (
              <div className="flex items-center gap-2">
                Это занимает дольше обычного — проверьте подключение.
                <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={reload}>
                  Повторить
                </Button>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="text-xs border border-destructive/40 rounded-lg px-3 py-2 space-y-1.5">
            <div className="text-sm font-medium text-destructive">Не удалось загрузить сводку</div>
            <div className="text-muted-foreground break-words">{error}</div>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={reload}>
              Повторить
            </Button>
          </div>
        )}

        {data?.resultsDenied && (
          <div className="text-xs border rounded-lg px-3 py-2 bg-card">
            <div className="text-sm font-medium">Часть протоколов недоступна</div>
            <div className="text-muted-foreground">
              Роли не выдано право «Чтение» на часть типов опросов. Назначения и доступные результаты показаны ниже.
            </div>
          </div>
        )}

        {data && data.warnings.length > 0 && (
          <div className="text-xs border border-amber-500/40 rounded-lg px-3 py-2">
            <div className="text-sm font-medium">Не удалось прочитать часть методик</div>
            <div className="text-muted-foreground break-words">
              {data.warnings.join(', ')}. Остальные данные сводки доступны — повторите обновление позже.
            </div>
          </div>
        )}

        {data && rows.length === 0 && (
          <div className="text-xs border rounded-lg px-3 py-4 bg-card">
            <div className="text-sm font-medium">Пока нет результатов и назначений</div>
            <div className="text-muted-foreground mt-1">
              Выберите пациента слева и назначьте методику на вкладке «Тесты и результаты» — прохождения
              и ожидающие назначения появятся здесь.
            </div>
          </div>
        )}

        {data && rows.length > 0 && visibleRows.length === 0 && (
          <div className="text-xs border rounded-lg px-3 py-3 bg-card flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground">
              {query ? 'По поиску ничего не найдено.' : 'По выбранному фильтру ничего не найдено.'}
            </span>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={resetFilters}>
              Сбросить отбор
            </Button>
          </div>
        )}

        {data && visibleRows.length > 0 && (
          <div className="space-y-1.5">
            {visibleRows.map((row) => {
              const StatusIcon = STATUS_ICON[row.status];
              return (
                <article key={row.key} className="rounded-lg border bg-card px-3 py-2">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-sm font-medium">{row.methodName}</span>
                        {row.methodCode && row.methodCode !== row.methodName && (
                          <span className="text-[10px] text-muted-foreground">{row.methodCode}</span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {row.date
                          ? formatValue(row.date, '', row.kind === 'result' ? 'timestamp' : 'date')
                          : '—'}
                        <span> · </span>
                        {row.patientId ? (
                          <button
                            type="button"
                            className="hover:underline"
                            onClick={() => onOpenResult(row.patientId, row.patientName)}
                            title="Открыть результаты пациента"
                          >
                            {row.patientName}
                          </button>
                        ) : (
                          <span>{row.patientName}</span>
                        )}
                      </div>
                    </div>
                    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] ${STATUS_CLASS[row.status]}`}>
                      <StatusIcon className="h-3 w-3" />
                      {STATUS_LABEL[row.status]}
                    </span>
                  </div>
                  <div className="mt-1 flex items-start gap-2">
                    <div className="min-w-0 flex-1 whitespace-pre-wrap break-words text-xs">
                      {resultText(row) || '—'}
                    </div>
                    <div className="flex shrink-0 items-center gap-0.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        disabled={!row.patientId}
                        onClick={() => onOpenPatient(row.patientId, row.patientName)}
                        title="Карточка пациента"
                      >
                        <UserRound className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        disabled={!row.patientId}
                        onClick={() => onOpenResult(row.patientId, row.patientName)}
                        title="Результаты пациента"
                      >
                        <FlaskConical className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {data && data.truncated && (
          <div className="text-[11px] text-muted-foreground">
            Показаны последние {data.rows.length} из {data.rowCount} строк; строки, требующие внимания,
            и просроченные назначения сохранены.
          </div>
        )}

        {data && (
          <div className="text-[11px] text-muted-foreground">
            пациентов в картотеке: {data.patientsTotal} · методик в банке: {data.methodsTotal}
          </div>
        )}
      </div>
    </div>
  );
}
